import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { Button, Form } from "@dpdgroupuk/mydpd-ui";
import "./styles.scss"; // fetch 100 items(max per postcode) to save correct ordering when click show more(sorting is on the ui part)

const MAX_ADDRESSES_PER_POSTCODE = 100;

const AddressAutocomplete = ({
  onFindClick,
  buttonText,
  label,
  helperText,
  input,
  meta,
  disabled,
  readonly,
  required,
  id,
  search,
  optionLabelMapper,
  labelKey,
  paginationText,
  pageSize,
  onSelectionChange,
  maxLength,
  withAutocomplete,
  shouldSearch,
  showFindButton,
  classes,
  getSearchQuery,
  fetchAll
}) => {
  const inputValue = useRef(input.value);
  const promiseRef = useRef();
  const typeaheadRef = useRef(null);
  const getSearchQueryRef = useRef(getSearchQuery);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState({
    page: 1
  });
  const abortIfPending = useCallback(() => {
    if (promiseRef.current && typeof promiseRef.current.abort === "function") {
      promiseRef.current.abort();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [promiseRef.current]);
  const searchWithStatusHandler = useCallback(params => {
    abortIfPending();
    const searching = search(params);
    searching.finally(() => {
      promiseRef.current = null;
    });
    promiseRef.current = searching;
    return searching;
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [abortIfPending]);
  useEffect(() => abortIfPending, []); // NOTE: Not everything is in deps to preserve correct sync between search and abort controller

  const handleSearch = useCallback(() => {
    if (disabled || readonly) return;

    if (!isLoading) {
      setIsLoading(true);
      setOptions([]);
    }

    const valueWithoutSpaces = inputValue.current ? inputValue.current.replaceAll(" ", "") : inputValue.current;
    setQuery({
      value: valueWithoutSpaces,
      page: 1
    });

    if (valueWithoutSpaces && shouldSearch(inputValue.current)) {
      searchWithStatusHandler({ ...getSearchQueryRef.current(valueWithoutSpaces),
        pageSize: fetchAll ? MAX_ADDRESSES_PER_POSTCODE : pageSize + 1
      }).then(data => {
        setOptions(data);
      }).finally(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [pageSize, disabled, readonly]);
  const formattedValue = input.value?.toUpperCase()?.replaceAll(" ", ""); // NOTE: Not everything is in deps to prevent redundant rerender

  useEffect(() => {
    inputValue.current = input.value;

    if (!input.value || input.value === "") {
      abortIfPending();
      setIsLoading(false);
      setOptions([]);
    } else if (!isLoading && !meta.active && withAutocomplete) {
      handleSearch();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [formattedValue, abortIfPending, withAutocomplete]);
  const handlePagination = useCallback(() => {
    if (!isLoading) {
      setIsLoading(true);
    }

    if (fetchAll && options.length <= MAX_ADDRESSES_PER_POSTCODE) {
      return setIsLoading(false);
    }

    searchWithStatusHandler({ ...getSearchQueryRef.current(query.value),
      page: query.page + 1,
      pageSize: pageSize + 1
    }).then(data => {
      setOptions(options.concat(data));
      setQuery({
        value: query.value,
        page: query.page + 1
      });
    }).catch(e => {
      setOptions([]);
      throw e;
    }).finally(() => {
      setIsLoading(false);
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, query, searchWithStatusHandler, pageSize]);
  const handleInputChange = useCallback(value => {
    abortIfPending();
    setOptions([]);
    input.onChange(value);

    if (shouldSearch(value) && value.length > 4) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [input, promiseRef, shouldSearch]);
  return /*#__PURE__*/React.createElement("div", {
    className: "mydpd-postcode-finder-container"
  }, withAutocomplete ? /*#__PURE__*/React.createElement(Form.Autocomplete, {
    label: label,
    helperText: helperText,
    meta: meta,
    input: { ...input,
      onBlur: () => {
        input.onBlur(typeaheadRef.current.state.text);
        typeaheadRef.current?.blur();
      }
    },
    required: required,
    disabled: disabled,
    readonly: readonly,
    typeaheadRef: typeaheadRef,
    id: id,
    isLoading: isLoading,
    minLength: 5,
    delay: 1500,
    onSearch: handleSearch,
    onInputChange: handleInputChange,
    options: options,
    onSelectionChange: onSelectionChange,
    paginationText: paginationText,
    pageSize: pageSize,
    onPaginate: handlePagination,
    labelKey: labelKey,
    optionLabelMapper: optionLabelMapper,
    maxLength: maxLength,
    classes: classes
  }) : /*#__PURE__*/React.createElement(Form.MaterialTextInput, {
    label: label,
    helperText: helperText,
    meta: meta,
    input: input,
    required: required,
    disabled: disabled,
    readonly: readonly,
    maxLength: maxLength
  }), showFindButton && /*#__PURE__*/React.createElement(Button, {
    variant: "dark",
    className: "find-button",
    onClick: onFindClick,
    disabled: disabled || readonly
  }, buttonText));
};

export const propTypes = {
  id: PropTypes.string.isRequired,
  onFindClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  buttonText: PropTypes.string,
  readonly: PropTypes.bool,
  label: PropTypes.string,
  helperText: PropTypes.string,
  input: PropTypes.object,
  meta: PropTypes.object,
  required: PropTypes.bool,
  optionLabelMapper: PropTypes.func,
  labelKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  paginationText: PropTypes.string,
  pageSize: PropTypes.number,
  onSelectionChange: PropTypes.func,
  search: PropTypes.func.isRequired,
  maxLength: PropTypes.number,
  withAutocomplete: PropTypes.bool,
  shouldSearch: PropTypes.func,
  showFindButton: PropTypes.bool,
  fetchAll: PropTypes.bool,
  classes: PropTypes.object,
  getSearchQuery: PropTypes.func
};
AddressAutocomplete.propTypes = propTypes;
AddressAutocomplete.defaultProps = {
  buttonText: "Find",
  paginationText: "Show more",
  pageSize: 10,
  shouldSearch: value => true,
  showFindButton: true,
  fetchAll: true,
  classes: {},
  getSearchQuery: value => ({
    postcode: value
  })
};
export default AddressAutocomplete;